// Review an AI rewrite before applying it: before/after side-by-side, the
// model's rationale, and any citations it added. Accept applies via the editor
// handle; reject discards. (Side-by-side rather than a char diff keeps it simple
// and dependency-free for v1.)
import { useTranslation } from "react-i18next";
import { Button, Spinner } from "@/components/ui";

interface Props {
  oldText: string;
  newText: string;
  rationale: string;
  citationsAdded: string[];
  busy: boolean;
  onAccept: () => void;
  onReject: () => void;
}

export function EditReviewModal({
  oldText,
  newText,
  rationale,
  citationsAdded,
  busy,
  onAccept,
  onReject,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">{t("Review the proposed edit")}</h2>
          <button type="button" onClick={onReject} className="text-sm text-ink-faint hover:text-ink">
            ✕
          </button>
        </div>
        {rationale && <p className="px-4 pt-3 text-sm text-ink-soft">{rationale}</p>}
        {citationsAdded.length > 0 && (
          <p className="px-4 pt-1 text-xs text-ink-faint">
            {t("Citations added")}: {citationsAdded.map((c) => `\\cite{${c}}`).join(", ")}
          </p>
        )}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-auto p-4 md:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-rose-700">{t("Before")}</p>
            <pre className="h-full overflow-auto whitespace-pre-wrap rounded-lg bg-rose-50/50 p-3 text-xs leading-snug text-ink">
              {oldText}
            </pre>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">{t("After")}</p>
            <pre className="h-full overflow-auto whitespace-pre-wrap rounded-lg bg-emerald-50/50 p-3 text-xs leading-snug text-ink">
              {newText}
            </pre>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-black/5 px-4 py-3">
          <Button variant="secondary" size="sm" onClick={onReject} disabled={busy}>
            {t("Reject")}
          </Button>
          <Button size="sm" onClick={onAccept} disabled={busy}>
            {busy ? <Spinner className="h-3 w-3" /> : t("Accept & apply")}
          </Button>
        </div>
      </div>
    </div>
  );
}
